<template>
  <Page title="Release Note Generator">
    <!-- Organization -->
    <b-input-group prepend="Organization">
      <b-form-select v-model="selectedOrganization">
        <b-form-select-option :value="null" disabled>
          Select an organization
        </b-form-select-option>
        <b-form-select-option v-for="organization in organizationOptions" :value="organization.value"
          :key="organization.key">
          {{ organization.value }}
        </b-form-select-option>
      </b-form-select>
    </b-input-group>
    <div class="btn-container">
      <div class="btn-wrapper">
        <b-button type="button" variant="outline" v-b-modal.organization-modal>
          + Add organization
        </b-button>
      </div>
      <div class="btn-wrapper">
        <b-button v-if="selectedOrganization && selectedOrganization !== NVENTIVE_ORG" type="button" variant="outline" v-b-modal.update-pat-modal>
          Update Personal Access Token
        </b-button>
      </div>
    </div>
    <!-- Project -->
    <b-input-group prepend="Project">
      <b-form-input
        v-model="projectInput"
        :disabled="!selectedOrganization"
        placeholder="Type to search projects..."
        @input="onProjectInput"
        @blur="onProjectBlur"
        @focus="onProjectFocus"
        autocomplete="off"
      />
      <!-- Autocomplete dropdown -->
      <div v-if="showProjectDropdown && filteredProjectOptions.length > 0" class="autocomplete-dropdown">
        <div
          v-for="project in filteredProjectOptions"
          :key="project.value.id"
          class="autocomplete-item"
          @click="onProjectClick(project)"
        >
          {{ project.text }}
        </div>
      </div>
    </b-input-group>
    <!--Repository-->
    <b-input-group prepend="Repository">
      <b-form-select v-model="selectedRepository" :disabled="!selectedProject || !selectedOrganization">
        <b-form-select-option :value="null" disabled>
          Select a repository
        </b-form-select-option>
        <b-form-select-option v-for="repo in repositoryOptions" :value="repo.value" :key="repo.value.id">
          {{ repo.text }}
        </b-form-select-option>
      </b-form-select>
    </b-input-group>
    <!--Commit Hash Entry-->
    <div class="commit-hash-group">
      <CommitHashInput
        v-model="commitHash1"
        label="Commit Hash 1"
        placeholder="Enter first commit hash"
        :disabled="!selectedRepository || !selectedProject || !selectedOrganization"
        :validation-state="!commitHash1 ? null : isCommitHash1Valid"
        :branches="branches"
        position="left"
      />
      <CommitHashInput
        v-model="commitHash2"
        label="Commit Hash 2"
        placeholder="Enter second commit hash"
        :disabled="!selectedRepository || !selectedProject || !selectedOrganization"
        :validation-state="!commitHash2 ? null : isCommitHash2Valid"
        :branches="branches"
        position="right"
      />
    </div>
    <!-- Commit Info -->
    <CommitInfo v-if="firstCommitData" :commitData="firstCommitData" :options="branches"/>
    <CommitInfo v-if="secondCommitData" :commitData="secondCommitData" :options="branches"/>

    <!-- Submit Button-->
    <div class="mt-3 button-row">
      <b-button v-if="!isReleaseNoteLoading && releaseNoteResult" type="button" variant="outline-danger" size="sm"
        @click="clearReleaseNoteResult" class="clear-button">
        Clear Response
      </b-button>
      <b-button v-if="!releaseNoteResult" type="button" variant="primary"
        :disabled="!(isCommitHash1Valid && isCommitHash2Valid) || isReleaseNoteLoading" @click="onSubmitReleaseNotes"
        class="submit-button">
        <b-spinner v-if="isReleaseNoteLoading" small class="mr-2"></b-spinner>
        {{ isReleaseNoteLoading ? 'Generating...' : 'Generate Release Notes' }}
      </b-button>
      <b-button v-if="!isReleaseNoteLoading && releaseNoteResult" variant="outline-secondary" size="sm"
        @click="copyToClipboard" class="copy-button">
        Copy Release Notes
      </b-button>
    </div>

    <!-- Release Note Result -->
    <div class="release-note-result mt-4">

      <template v-if="releaseNoteResult">
                <div class="markdown-container">
          <Vue3MarkdownIt :source="releaseNoteResult.answer" />
        </div>
        <h5 v-if="streamingReleaseNoteCompleted" class="mt-4 mb-3">Generation Statistics</h5>
        <b-list-group v-if="streamingReleaseNoteCompleted" class="mt-3">
          <b-list-group-item>
            <strong>Cost:</strong> {{ releaseNoteResult.cost }}
          </b-list-group-item>
          <b-list-group-item>
            <strong>Number of Api request:</strong> {{ releaseNoteResult.nb_requests }}
          </b-list-group-item>
          <b-list-group-item>
            <strong>Input tokens:</strong> {{ releaseNoteResult.input_tokens }}
          </b-list-group-item>
          <b-list-group-item>
            <strong>Output tokens:</strong> {{ releaseNoteResult.output_tokens }}
          </b-list-group-item>
        </b-list-group>
      </template>
    </div>
    <!-- Modals -->
    <b-modal id="organization-modal" title="Add Organization" ok-title="Submit" @ok="fetchOrganizationData"
      @hidden="clearOrganizationInput">
      <div class="input-icon">
        <b-form-input v-model="organizationName" placeholder="Enter the organization name"
          :state="organizationInputError">
        </b-form-input>
        <div class="tooltip-icon" id="name-tooltip">
          <InfoCircleIcon />
        </div>
        <b-tooltip target="name-tooltip" triggers="hover">
          The organization name is the part in the URL that follows the domain name. In this example :
          'https://dev.azure.com/{your_organization_name}', the domain name is 'dev.azure.com' and the organization
          name
          is '{your_organization_name}'. Copy that part and paste it here.
        </b-tooltip>
      </div>
      <div class="input-icon">
        <b-form-input class="last" v-model="personalAccessToken" placeholder="Enter the personal access token"
          :state="organizationInputError">
        </b-form-input>
        <div @click="openTab" class="tooltip-icon" id="pat-tooltip" data-html="true">
          <InfoCircleIcon />
        </div>
        <b-tooltip target="pat-tooltip" triggers="hover">
          Click here to see how to create a new personal access token.
        </b-tooltip>
      </div>
      <div class="helper-text">
        When creating a new token, under Scopes section, select "Custom defined". Find "Work Items" and only select
        "Read".
      </div>
      <div class="error" v-if="organizationInputError" variant="danger">
        Organization and/or token not invalid.
      </div>
    </b-modal>
     <b-modal
      id="update-pat-modal"
      title="Update Personal Access Token"
      ok-title="Save"
      @ok="updatePersonalAccessToken"
      @hidden="clearNewPersonalAccessToken">
      <h5>
        {{this.selectedOrganization}}
      </h5>
      <b-form-input
        class="last"
        v-model="newPersonalAccessToken"
        placeholder="Enter the new personal access token"
        :state="tokenUpdateInputError">
      </b-form-input>
      <div class="error" v-if="tokenUpdateInputError === false" variant="danger">
        Token is invalid.
      </div>
    </b-modal>
  </Page>
</template>

<script>
import { mapStores } from 'pinia';
import useApiStore from '@/stores/api';
import {
  encryptToken,
  decryptToken,
} from '@/helpers/encryptDecrypt';
import {
  LOCAL_STORAGE_KEY,
  AZURE_FUNTION_URL,
  AzureClientidScopes,
  NVENTIVE_ORG,
} from '@/settings';
import Page from '@/components/Page.vue';
import InfoCircleIcon from '@/components/UI/InfoCircleIcon.vue';
import 'vue3-toastify/dist/index.css';
import CommitInfo from '@/components/UI/CommitInfo.vue';
import CommitHashInput from '@/components/UI/CommitHashInput.vue';
import { toast } from 'vue3-toastify';
import Vue3MarkdownIt from 'vue3-markdown-it';
import Fuse from 'fuse.js';
import useAuthStore from '../stores/checkAuth';

export default {
  name: 'ReleaseNoteGenerator',
  components: {
    InfoCircleIcon,
    CommitInfo,
    CommitHashInput,
    Page,
    Vue3MarkdownIt,
  },
  data() {
    return {
      organizationName: '',
      personalAccessToken: '',
      selectedOrganization: null,
      selectedProject: null,
      organizationInputError: null,
      organizationOptions: [],
      projectOptions: [],
      repositoryOptions: [],
      selectedRepository: null,
      commitHash1: '',
      commitHash2: '',
      firstCommitData: null,
      secondCommitData: null,
      isCommitHash1Valid: false,
      isCommitHash2Valid: false,
      releaseNoteResult: null,
      isReleaseNoteLoading: false,
      streamingReleaseNoteCompleted: false,
      projectInput: '',
      showProjectDropdown: false,
      filteredProjectOptions: [],
      projectFuse: null,
      branches: [],
      NVENTIVE_ORG,
    };
  },
  computed: {
    ...mapStores(useApiStore),
    ...mapStores(useAuthStore),
  },
  created() {
    this.getOrganizationOptions();
  },
  methods: {
    openTab() {
      window.open('https://learn.microsoft.com/en-us/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate?toc=%2Fazure%2Fdevops%2Forganizations%2Ftoc.json&view=azure-devops&tabs=Windows', '_blank');
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.releaseNoteResult.answer);
        this.showToast('success', 'Release notes copied to clipboard!');
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.releaseNoteResult.answer;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.showToast('success', 'Release notes copied to clipboard!');
      }
    },
    async fetchOrganizationData(e) {
      e.preventDefault();
      this.organizationName = this.organizationName.trim();

      let orgAndTokenAreValid = false;

      orgAndTokenAreValid = await this.apiStore.checkOrgnizationAndTokenValidity(this.personalAccessToken, this.organizationName);

      if (orgAndTokenAreValid) {
        this.updateOrganizationOptions();
        this.clearOrganizationInput();
        this.organizationInputError = true;
        this.$bvModal.hide('organization-modal');
      } else {
        this.organizationInputError = false;
      }
    },
    async getOrganizationOptions() {
      let orgKeys = null;
      let organizations = localStorage.getItem(LOCAL_STORAGE_KEY);
      let oganizationStorage;
      if (organizations) {
        organizations = JSON.parse(organizations);
        oganizationStorage = Object.keys(organizations)
          .map((key) => ({
            key: organizations[key],
            value: key,
          }));
        orgKeys = Object.keys(organizations);
      }
      const secretKey = process.env.VUE_APP_ENCRYPTION_DECRYPTION_KEY;

      for (let i = 0; i < oganizationStorage?.length; i += 1) {
        const org = oganizationStorage[i];
        const decryptedPAT = decryptToken(org.key, secretKey);
        // eslint-disable-next-line no-await-in-loop
        const isValid = await this.apiStore.checkOrgnizationAndTokenValidity(decryptedPAT, org.value);
        if (isValid) {
          this.organizationOptions.push(org);
        }
      }

      if (orgKeys === null || !orgKeys.includes(NVENTIVE_ORG)) {
        const organization = {
          value: NVENTIVE_ORG,
          key: undefined,
        };
        this.organizationOptions.push(organization);
      }

      this.organizationOptions.sort((a, b) => a.value.localeCompare(b.value));
    },
    updateOrganizationOptions() {
      let organization = {};
      let organizations = localStorage.getItem(LOCAL_STORAGE_KEY);

      if (organizations) {
        organizations = JSON.parse(organizations);
      } else {
        organizations = {};
      }

      const secretKey = process.env.VUE_APP_ENCRYPTION_DECRYPTION_KEY;
      const encryptedPAT = encryptToken(this.personalAccessToken, secretKey);

      organizations[this.organizationName] = encryptedPAT;
      organization = {
        value: this.organizationName,
        key: encryptedPAT,
      };

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(organizations));
      this.organizationOptions.push(organization);
      this.organizationOptions.sort((a, b) => a.value.localeCompare(b.value));
    },
    getProjectOptions() {
      if (!this.apiStore.coreApi) return;

      this.apiStore.coreApi.getProjects('', 1000).then((result) => {
        result.sort((a, b) => a.name.localeCompare(b.name));
        this.projectOptions = result.map((r) => ({
          value: r,
          text: r.name,
        }));
        this.filteredProjectOptions = [...this.projectOptions];
        this.projectFuse = new Fuse(this.projectOptions, {
          keys: ['text'],
          threshold: 0.4,
          distance: 100,
          includeScore: true,
          minMatchCharLength: 1,
          shouldSort: true,
          findAllMatches: false,
          ignoreLocation: true,
          ignoreFieldNorm: false,
        });
      });
    },
    async updatePersonalAccessToken(e) {
      e.preventDefault();
      let organizations;
      let orgAndTokenAreValid = false;
      // eslint-disable-next-line max-len
      orgAndTokenAreValid = await this.apiStore.checkOrgnizationAndTokenValidity(this.newPersonalAccessToken, this.selectedOrganization);

      if (orgAndTokenAreValid === true) {
        organizations = localStorage.getItem(LOCAL_STORAGE_KEY);

        if (organizations !== null) {
          organizations = JSON.parse(organizations);
        } else {
          organizations = {};
        }

        const secretKey = process.env.VUE_APP_ENCRYPTION_DECRYPTION_KEY;
        const encryptedPAT = encryptToken(this.newPersonalAccessToken, secretKey);

        organizations[this.selectedOrganization] = encryptedPAT;
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(organizations));
        this.clearNewPersonalAccessToken();

        toast('Token updated successfully!', {
          type: 'success',
          position: 'bottom-center',
          theme: 'light',
          autoClose: 3000,
        });
        this.$bvModal.hide('update-pat-modal');
      } else {
        toast('Token is invalid!', {
          type: 'error',
          position: 'bottom-center',
          theme: 'light',
          autoClose: 3000,
        });
        this.tokenUpdateInputError = false;
      }
    },
    async getRepositoryOptions() {
      try {
        const result = await this.apiStore.gitApi.getRepositories(this.selectedProject.name);
        result.sort((a, b) => a.name.localeCompare(b.name));
        this.repositoryOptions = result.map((r) => ({
          value: r,
          text: r.name,
        }));
        this.selectedRepository = this.repositoryOptions.length > 0
          ? this.repositoryOptions[0].value : null;
      } catch (error) {
        console.error('Error fetching repositories:', error);
        this.showToast('error', 'Failed to fetch repositories');
      }
    },
    async getBranches() {
      const refs = await this.apiStore.gitApi.getRefs(this.selectedRepository.id);
      if (!refs || refs.length === 0) {
        this.branches = [];
        return;
      }
      this.branches = refs.filter((ref) => ref.name.startsWith('refs/heads/')).map((branch) => ({
        name: branch.name.replace('refs/heads/', ''),
        latestCommit: branch.objectId,
      })).sort((a, b) => a.name.localeCompare(b.name));
    },
    async fetchCommitData(commitHash, repo) {
      const COMMIT_HASH_REGEX = /^[a-fA-F0-9]{40}$/;
      if (!COMMIT_HASH_REGEX.test(commitHash)) {
        return null;
      }
      if (!this.apiStore.gitApi) return null;
      let result = null;
      try {
        result = await this.apiStore.gitApi.getCommit(commitHash, repo.id);
        if (!result) {
          this.showToast('error', 'Commit not found');
        }
      } catch {
        this.showToast('error', 'Error fetching commit data');
      }
      return result;
    },
    clearOrganizationInput() {
      this.organizationName = '';
      this.personalAccessToken = '';
      this.organizationInputError = false;
    },
    clearOrganizationSpecificData() {
      this.projectOptions = [];
      this.filteredProjectOptions = [];
      this.clearProjectSpecificData();
    },
    clearProjectSpecificData() {
      this.repositoryOptions = [];
      this.selectedRepository = null;
      this.projectInput = '';
      this.selectedProject = null;
      this.showProjectDropdown = false;
      this.clearReleaseNoteResult();
      this.clearCommitData();
    },
    clearCommitData() {
      this.firstCommitData = null;
      this.secondCommitData = null;
      this.isCommitHash1Valid = null;
      this.isCommitHash2Valid = null;
      this.commitHash1 = '';
      this.commitHash2 = '';
    },
    clearReleaseNoteResult() {
      this.releaseNoteResult = null;
      this.isReleaseNoteLoading = false;
      this.streamingReleaseNoteCompleted = false;
    },
    clearNewPersonalAccessToken() {
      this.newPersonalAccessToken = '';
      this.tokenUpdateInputError = null;
    },
    onProjectInput(val) {
      this.showProjectDropdown = true;
      if (!val) {
        this.filteredProjectOptions = [...this.projectOptions];
        this.selectedProject = null;
        return;
      }

      const results = this.projectFuse.search(val);
      this.filteredProjectOptions = results.map((result) => result.item);

      if (this.filteredProjectOptions.length === 0) {
        this.filteredProjectOptions = [...this.projectOptions];
      }
      const exactMatch = this.projectOptions.find((p) => p.text.toLowerCase() === val.toLowerCase());
      this.selectedProject = exactMatch ? exactMatch.value : null;
    },
    onProjectBlur() {
      setTimeout(() => {
        if (!this.selectedProject) {
          this.showProjectDropdown = false;
          this.projectInput = '';
          return;
        }
        this.onProjectInput(this.projectInput);
        this.showProjectDropdown = false;
        this.getRepositoryOptions();
      }, 200);
    },
    onProjectFocus() {
      if (this.projectOptions.length > 0) {
        this.showProjectDropdown = true;
        this.filteredProjectOptions = [...this.projectOptions];
      }
    },
    onProjectClick(project) {
      this.projectInput = project.text;
      this.selectedProject = project.value;
      this.showProjectDropdown = false;
    },
    showToast(type, message, autoCloseTimeSecond = 3) {
      toast(message, {
        type,
        position: 'bottom-center',
        theme: 'light',
        autoClose: autoCloseTimeSecond * 1000,
      });
    },
    async onSubmitReleaseNotes() {
      let azureBearerToken;
      if (this.selectedOrganization === NVENTIVE_ORG) {
        azureBearerToken = this.apiStore.getBearerToken;
      } else if (!this.personalAccessToken) {
        this.showToast('error', 'You need to set a Personal Access Token for this organization');
        return;
      }
      const payload = JSON.stringify({
        bearer_token: azureBearerToken,
        organization_name: this.selectedOrganization,
        pat: this.personalAccessToken,
        project: this.selectedProject.name,
        repository_name: this.selectedRepository.name,
        first_commit_id: this.commitHash1,
        second_commit_id: this.commitHash2,
      });
      const token = await this.authStore.getAccessToken(AzureClientidScopes);
      if (!token) {
        this.showToast('error', 'Error getting access token');
        return;
      }
      this.isReleaseNoteLoading = true;
      try {
        const response = await fetch(`${AZURE_FUNTION_URL}request`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: payload,
        });
        if (!response.ok) {
          this.clearReleaseNoteResult();
          const errorMessage = await response.json();
          console.error('Error response:', errorMessage);
          this.showToast('error', errorMessage?.error || 'An unknow error occurred try again', 10);
          return;
        }

        this.releaseNoteResult = {
          answer: '',
          cost: 0,
          nb_requests: 0,
          input_tokens: 0,
          output_tokens: 0,
        };
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          this.isReleaseNoteLoading = false;
          if (done) {
            this.streamingReleaseNoteCompleted = true;
            return;
          }
          const chunks = decoder.decode(value, { stream: true }).split('\n');
          for (let i = 0; i < chunks.length - 1; i += 1) {
            const chunkData = JSON.parse(chunks[i]);
            if (chunkData?.content) {
              this.releaseNoteResult.answer += chunkData.content;
            }
            if (chunkData?.nb_requests) {
              this.releaseNoteResult.nb_requests = chunkData.nb_requests;
            }
            if (chunkData?.cost) {
              this.releaseNoteResult.cost = chunkData.cost;
            }
            if (chunkData?.input_tokens) {
              this.releaseNoteResult.input_tokens = chunkData.input_tokens;
            }
            if (chunkData?.output_tokens) {
              this.releaseNoteResult.output_tokens = chunkData.output_tokens;
            }
          }
        }
      } catch (err) {
        this.clearReleaseNoteResult();
        this.showToast('error', err?.message || 'An unknow error occured, try again', 10);
      } finally {
        this.isReleaseNoteLoading = false;
      }
    },
  },
  watch: {
    async selectedOrganization() {
      if (!this.selectedOrganization) return;
      this.clearOrganizationSpecificData();
      const organization = this.organizationOptions
        .find((opt) => opt.value === this.selectedOrganization);

      if (!organization) {
        return;
      }

      const secretKey = process.env.VUE_APP_ENCRYPTION_DECRYPTION_KEY;
      if (organization?.key) {
        const decryptedPAT = decryptToken(organization.key, secretKey);
        this.personalAccessToken = decryptedPAT;
      }
      await this.apiStore.setOrganization(organization); // Set the organization in the API store, it handles the authentication automatically
      this.getProjectOptions();
    },
    async selectedProject(newVal) {
      this.selectedProject = newVal;
      this.clearCommitData();
      this.clearReleaseNoteResult();
    },
    async selectedRepository(newVal) {
      this.selectedRepository = newVal;
      this.clearCommitData();
      this.clearReleaseNoteResult();
      if (newVal) {
        await this.getBranches();
      }
    },
    async commitHash1(newVal) {
      this.clearReleaseNoteResult();
      this.firstCommitData = null;
      this.isCommitHash1Valid = false;
      if (this.commitHash2 && newVal === this.commitHash2) {
        this.showToast('error', 'Commit Hash 1 cannot be the same as Commit Hash 2');
        return;
      }
      const res = await this.fetchCommitData(newVal, this.selectedRepository);
      if (res) {
        this.firstCommitData = res;
        this.isCommitHash1Valid = true;
      }
    },
    async commitHash2(newVal) {
      this.clearReleaseNoteResult();
      this.secondCommitData = null;
      this.isCommitHash2Valid = false;
      if (this.commitHash1 && newVal === this.commitHash1) {
        this.showToast('error', 'Commit Hash 2 cannot be the same as Commit Hash 1');
        return;
      }
      const res = await this.fetchCommitData(newVal, this.selectedRepository);
      if (res) {
        this.secondCommitData = res;
        this.isCommitHash2Valid = true;
      }
    },
  },
};

</script>
<style scoped>
.commit-hash-group {
  display: flex;
  gap: 1rem;
}
.button-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.clear-button {
  flex: 0 0 auto;
}

.submit-button {
  flex: 1;
  max-width: 300px;
  margin: 0 auto;
}

.copy-button {
  flex: 0 0 auto;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-spinner {
  width: 3rem !important;
  height: 3rem !important;
}
.markdown-container {
  border: 0.1rem solid #cbd5e0;
  background-color: #f7fafc;
  border-radius: 0.375rem;
  padding: 1rem;
  margin-bottom: 1rem;
}
.autocomplete-dropdown {
  position: absolute;
  z-index: 1000;
  background-color: white;
  border: 1px solid #bdd5ee;
  border-radius: 0.375rem;
  width: 100%;
  max-height: 600%;
  overflow-y: auto;
  margin-top: 2.5rem;
}

.autocomplete-item {
  padding: 0.5rem 1rem;
  cursor: pointer;
}

.autocomplete-item:hover {
  background-color: #f1f5f9;
}

.autocomplete-item:last-child {
  border-bottom-left-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}
</style>
